<template>
  <!-- Status Selection Sheet -->
  <Sheet v-model="isOpen" title="Watch Status" size="lg" :fullscreen="fullscreen" content-class="p-0">
    <!-- Title Strip -->
    <div v-if="title" class="p-4 pb-0">
      <div class="overflow-hidden rounded-lg border border-gray-700">
        <TitleStrip :title="title" :condensed="true" :dark="true" />
      </div>
    </div>

    <div class="space-y-1 px-4 pb-4 pt-3">
      <!-- Status buttons - always at top, TitleActionsBlock style -->
      <div class="flex gap-2 w-full pb-2">
        <!-- Watchlist - independent intent toggle (doc 27), can coexist
             with any history state -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 disabled:opacity-60 disabled:cursor-default"
          :class="
            loadingStatus === 'watchlist'
              ? 'bg-amber-500/20 border-amber-400/40'
              : isWatchlisted
                ? 'bg-amber-500/20 border-white/40 hover:bg-white/30'
                : 'bg-white/10 border-white/20 hover:bg-white/20 hover:border-amber-400 hover:border-b-4 hover:-mt-0.5'
          "
          @click="toggleWatchlistIntent">
          <fa-icon
            v-if="loadingStatus === 'watchlist'"
            icon="spinner-third"
            class="fa-spin text-xl text-amber-400" />
          <fa-icon
            v-else
            :icon="isWatchlisted ? ['fas', 'bookmark'] : ['far', 'bookmark']"
            class="text-xl"
            :class="isWatchlisted ? 'text-amber-400' : 'text-white/60'" />
          <span class="text-2xs font-bold text-white/90">Watchlist</span>
        </button>

        <!-- Watching -->
        <button
          v-if="usesLogSystem"
          type="button"
          :disabled="!!loadingStatus"
          class="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 disabled:opacity-60 disabled:cursor-default"
          :class="
            loadingStatus === 'in_progress'
              ? 'bg-cyan-500/20 border-cyan-400/40'
              : currentStatus === 'in_progress'
                ? 'bg-cyan-500/20 border-white/40 hover:bg-white/30'
                : 'bg-white/10 border-white/20 hover:bg-white/20 hover:border-cyan-400 hover:border-b-4 hover:-mt-0.5'
          "
          @click="changeStatusFromButtons('in_progress')">
          <fa-icon
            v-if="loadingStatus === 'in_progress'"
            icon="spinner-third"
            class="fa-spin text-xl text-cyan-400" />
          <fa-icon
            v-else
            :icon="
              currentStatus === 'in_progress'
                ? ['fas', 'play-circle']
                : ['far', 'play-circle']
            "
            class="text-xl"
            :class="
              currentStatus === 'in_progress'
                ? 'text-cyan-400'
                : 'text-white/60'
            " />
          <span class="text-2xs font-bold text-white/90">Watching</span>
        </button>

        <!-- Seen -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all cursor-pointer border-b-2 active:border-b-0 active:mt-0.5 disabled:opacity-60 disabled:cursor-default"
          :class="
            loadingStatus === 'seen'
              ? 'bg-green-500/20 border-green-400/40'
              : currentStatus === 'seen'
                ? 'bg-green-500/20 border-white/40 hover:bg-white/30'
                : 'bg-white/10 border-white/20 hover:bg-white/20 hover:border-green-400 hover:border-b-4 hover:-mt-0.5'
          "
          @click="changeStatusFromButtons('seen')">
          <fa-icon
            v-if="loadingStatus === 'seen'"
            icon="spinner-third"
            class="fa-spin text-xl text-green-400" />
          <fa-icon
            v-else
            :icon="
              currentStatus === 'seen'
                ? ['fas', 'hexagon-check']
                : ['far', 'hexagon-check']
            "
            class="text-xl"
            :class="
              currentStatus === 'seen' ? 'text-green-400' : 'text-white/60'
            " />
          <span class="text-2xs font-bold text-white/90">Seen</span>
        </button>
      </div>

      <!-- Error message -->
      <p
        v-if="errorStatus"
        class="text-xs text-red-400 text-center py-1">
        Something went wrong, try again
      </p>

      <!-- Seen-specific options -->
      <template v-if="currentStatus === 'seen'">
        <!-- Compressed seen date row -->
        <button
          type="button"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
          @click="openDateSheet">
          <fa-icon :icon="['far', 'calendar']" class="w-5 text-green-400" />
          <span class="flex-1 font-medium">
            <template v-if="logMetadata?.watchedOn">
              Seen : {{ formatMetadataDate(logMetadata.watchedOn) }}
            </template>
            <template v-else>When did you watch?</template>
          </span>
          <fa-icon
            :icon="['far', 'chevron-right']"
            class="text-xs text-gray-500" />
        </button>

        <!-- Rewatch option -->
        <button
          v-if="usesLogSystem"
          type="button"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-purple-500/20"
          @click="handleRewatch">
          <fa-icon
            :icon="['far', 'rotate-right']"
            class="w-5 text-purple-400" />
          <span class="flex-1 font-medium">Log a Rewatch</span>
        </button>
      </template>

      <!-- Did not finish indicator -->
      <div
        v-if="currentStatus === 'dropped'"
        class="flex gap-3 items-center px-4 py-3 mx-2 rounded-lg bg-red-500/20 text-red-400">
        <fa-icon :icon="['fas', 'circle-stop']" class="w-5" />
        <span class="font-medium">Did not finish</span>
      </div>

      <!-- Add/Edit Note option -->
      <button
        type="button"
        class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        @click="openNoteSheet">
        <fa-icon :icon="[standaloneNote ? 'fas' : 'far', 'note-sticky']" class="w-5 text-blue-400" />
        <span class="flex-1 font-medium">{{
          standaloneNote ? 'Edit Note' : 'Add Note'
        }}</span>
        <fa-icon
          :icon="['far', 'chevron-right']"
          class="text-xs text-gray-500" />
      </button>

      <!-- Did not finish + Clear status -->
      <div class="border-t border-white/10">
        <!-- Did not finish option (available for all states including no status) -->
        <button
          v-if="currentStatus !== 'dropped'"
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-red-400 rounded-lg transition-colors cursor-pointer hover:bg-red-500/20 disabled:opacity-60 disabled:cursor-default"
          @click="changeStatusFromButtons('dropped')">
          <fa-icon
            v-if="loadingStatus === 'dropped'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-stop']" class="w-5" />
          <span class="font-medium">Did not finish</span>
        </button>

        <!-- Clear status (clears history state and watchlist intent) -->
        <button
          v-if="currentStatus"
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-gray-400 rounded-lg transition-colors cursor-pointer hover:bg-white/10 disabled:opacity-60 disabled:cursor-default"
          @click="clearAllStatuses">
          <fa-icon
            v-if="loadingStatus === 'clearing'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-xmark']" class="w-5" />
          <span class="font-medium">Clear status</span>
        </button>
      </div>

      <!-- Multiple states warning -->
      <div
        v-if="hasMultipleStates"
        class="px-4 py-3 mt-2 mx-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
        <p class="text-xs text-amber-400 mb-2">
          <fa-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
          Also tracked as:
          {{
            allActiveStates
              .filter((s) => s !== currentStatus && s !== 'watchlist')
              .map(getStateName)
              .join(', ')
          }}
        </p>
        <button
          type="button"
          class="text-xs text-amber-400 hover:text-amber-300 font-medium"
          @click="clearAllStatuses">
          Clear all statuses
        </button>
      </div>
    </div>
  </Sheet>

  <!-- Date picker sub-sheet -->
  <Sheet
    v-model="showDateSheet"
    title="When did you watch?"
    size="md"
    content-class="px-4 pb-4">
    <DateQuickPicker
      :model-value="currentLog?.watched_on || null"
      @update:model-value="updateWatchedOn" />
    <template #footer>
      <button
        type="button"
        class="w-full py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors"
        @click="closeDateSheet">
        Done
      </button>
    </template>
  </Sheet>

  <!-- Metadata Sheet -->
  <LogMetadataSheet
    v-model="showMetadataSheet"
    :status="pendingStatus === 'rewatch' ? 'seen' : pendingStatus || 'seen'"
    :is-rewatch="pendingStatus === 'rewatch'"
    :show-date-field="pendingStatus !== 'watchlist'"
    :current-log="currentLog"
    :title-type="titleType"
    :title-name="titleName"
    :title="title"
    @save="handleMetadataSave"
    @skip="handleMetadataSkip" />

  <!-- Standalone Note Sheet -->
  <StandaloneNoteSheet
    v-model="showNoteSheet"
    :title="title"
    :title-type="titleType"
    :initial-note="standaloneNote"
    @saved="handleNoteSaved" />
</template>

<script setup>
import { ref, computed } from 'vue'
import { Sheet } from '@/components/Options'
import TitleStrip from '@/components/TitleStrip.vue'
import { LogMetadataSheet, StandaloneNoteSheet } from '@/components/Sheets'
import DateQuickPicker from '@/components/Pure/DateQuickPicker.vue'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreProfile } from '@/stores/storeProfile'
import { useHapticFeedback } from '@/composables/useHapticFeedback'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: Object,
    default: null
  },
  currentLog: {
    type: Object,
    default: null
  },
  titleType: {
    type: String,
    default: 'movie'
  },
  titleName: {
    type: String,
    default: ''
  },
  fullscreen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'statusChanged'])

const haptics = useHapticFeedback()
const storeTitleLogs = useStoreTitleLogs()
const storeTitlesUser = useStoreTitlesUser()
const storeLists = useStoreLists()
const storeProfile = useStoreProfile()

// Loading state for status buttons
const loadingStatus = ref(null)
const errorStatus = ref(null)

// Sheet visibility
const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

// Metadata sheet state
const showMetadataSheet = ref(false)
const pendingStatus = ref(null)

// Note sheet state
const showNoteSheet = ref(false)

// Date picker sub-sheet state
const showDateSheet = ref(false)

// Get standalone note from titles_user
const standaloneNote = computed(() => {
  if (!props.title) return null
  const titleUserData = storeTitlesUser.getTitleByTitleId({
    id: props.title.id,
    type: props.title.media_type || props.titleType
  })
  return titleUserData?.notes || null
})

// Open note sheet
const openNoteSheet = () => {
  isOpen.value = false
  haptics.light()
  setTimeout(() => {
    showNoteSheet.value = true
  }, 100)
}

// Handle note saved
const handleNoteSaved = (noteText) => {
  emit('statusChanged', { noteUpdated: true, note: noteText })
}

// Log system is always enabled
const usesLogSystem = computed(() => true)

// Watchlist intent (doc 27) - independent of the history state, with the
// legacy list system as fallback for items without logs
const isWatchlisted = computed(() => {
  if (!props.title) return props.currentLog?.state === 'watchlist'
  const tmdbType = props.title.media_type || props.titleType

  if (storeTitleLogs.isWatchlistedByTmdb(props.title.id, tmdbType)) {
    return true
  }

  const watchlist = storeLists.getSystemListByType('watchlist')
  if (!watchlist) return false
  return storeLists.items.some(
    (i) =>
      i.list_id === watchlist.id &&
      i.titles?.tmdb_id == props.title.id &&
      i.titles?.tmdb_type == tmdbType
  )
})

// Current status reads the history state first (doc 27 decision 6);
// watchlist stands in only when no history log exists
const currentStatus = computed(() => {
  if (props.title) {
    const historyState = storeTitleLogs.getHistoryStateByTmdb(
      props.title.id,
      props.title.media_type || props.titleType
    )
    if (historyState) return historyState
  }

  if (props.currentLog && props.currentLog.state !== 'watchlist') {
    return props.currentLog.state
  }

  if (isWatchlisted.value) return 'watchlist'

  return null
})

// Check if title has multiple different states (data issue)
const hasMultipleStates = computed(() => {
  if (!props.title) return false
  return storeTitleLogs.hasMultipleStatesByTmdb(
    props.title.id,
    props.title.media_type || props.titleType
  )
})

// Get all active states for display
const allActiveStates = computed(() => {
  if (!props.title) return []
  return storeTitleLogs.getAllActiveStatesByTmdb(
    props.title.id,
    props.title.media_type || props.titleType
  )
})

// Extract metadata from current log
const logMetadata = computed(() => {
  if (!props.currentLog) return null
  return {
    service: props.currentLog.watched_service || null,
    notes: props.currentLog.notes || null,
    watchedOn: props.currentLog.watched_on || null,
    tvSeason: props.currentLog.tv_season || null,
    tvEpisode: props.currentLog.tv_episode || null
  }
})

// Format date for display
const formatMetadataDate = (dateString) => {
  if (!dateString) return null
  try {
    const date = new Date(dateString + 'T12:00:00')
    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    })
  } catch (e) {
    return null
  }
}

// Handle watched_on date change for seen status
const updateWatchedOn = async (date) => {
  if (!props.currentLog?.id) return
  haptics.light()
  try {
    await storeTitleLogs.updateLog(props.currentLog.id, { watched_on: date })
    emit('statusChanged', { watchedOnUpdated: true })
  } catch (error) {
    console.error('Failed to update watched_on:', error)
  }
}

// Open date picker sub-sheet (dims parent without dismissing)
const openDateSheet = () => {
  haptics.light()
  showDateSheet.value = true
}

// Close date sheet
const closeDateSheet = () => {
  haptics.light()
  showDateSheet.value = false
}

// Show error briefly then clear
const flashError = (status) => {
  errorStatus.value = status
  haptics.error()
  setTimeout(() => {
    errorStatus.value = null
  }, 2000)
}

// Change history status from top buttons (sheet stays open)
// Tapping the active status clears it (toggle behavior) - clearing a
// history state leaves the watchlist intent alone (doc 27)
const changeStatusFromButtons = async (status) => {
  if (loadingStatus.value) return
  haptics.light()
  if (status === currentStatus.value) {
    loadingStatus.value = 'clearing'
    try {
      await clearHistoryStatus()
    } catch {
      flashError('clearing')
    } finally {
      loadingStatus.value = null
    }
    return
  }
  loadingStatus.value = status
  try {
    await applyStatus(status)
    emit('statusChanged', { status })
  } catch {
    flashError(status)
  } finally {
    loadingStatus.value = null
  }
}

// Toggle the watchlist intent without touching the history state (doc 27)
const toggleWatchlistIntent = async () => {
  if (loadingStatus.value) return
  haptics.light()
  loadingStatus.value = 'watchlist'
  try {
    if (isWatchlisted.value) {
      await removeWatchlistIntent()
      emit('statusChanged', { watchlisted: false })
    } else {
      await applyStatus('watchlist')
      emit('statusChanged', { watchlisted: true })
    }
  } catch {
    flashError('watchlist')
  } finally {
    loadingStatus.value = null
  }
}

// Handle rewatch
const handleRewatch = () => {
  isOpen.value = false
  haptics.light()
  pendingStatus.value = 'rewatch'
  showMetadataSheet.value = true
}

// Handle metadata save
const handleMetadataSave = async (metadata) => {
  if (!pendingStatus.value || !props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  try {
    if (pendingStatus.value === 'rewatch') {
      await storeTitleLogs.createLog(titleData, 'seen', metadata)
    } else {
      await applyStatus(pendingStatus.value, metadata)
    }

    emit('statusChanged', {
      status: pendingStatus.value === 'rewatch' ? 'seen' : pendingStatus.value,
      metadata
    })
  } catch (error) {
    console.error('Failed to apply status:', error)
  }

  pendingStatus.value = null
}

// Handle metadata skip
const handleMetadataSkip = async () => {
  if (!pendingStatus.value || !props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  try {
    if (pendingStatus.value === 'rewatch') {
      await storeTitleLogs.createLog(titleData, 'seen', {})
    } else {
      await applyStatus(pendingStatus.value)
    }

    emit('statusChanged', {
      status: pendingStatus.value === 'rewatch' ? 'seen' : pendingStatus.value
    })
  } catch (error) {
    console.error('Failed to apply status:', error)
  }

  pendingStatus.value = null
}

// Apply a status change
const applyStatus = async (status, metadata = {}) => {
  if (!props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  switch (status) {
    case 'seen':
      await storeTitleLogs.markAsSeen(titleData, metadata)
      break
    case 'in_progress':
      await storeTitleLogs.startWatching(titleData, metadata)
      break
    case 'watchlist':
      await storeTitleLogs.addToWatchlist(titleData, metadata)
      break
    case 'dropped':
      await storeTitleLogs.markAsDropped(titleData, metadata)
      break
  }
}

// Clear the active history state only - the watchlist intent survives
const clearHistoryStatus = async () => {
  if (!props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  const historyState = storeTitleLogs.getHistoryStateByTmdb(
    props.title.id,
    titleData.type
  )
  if (historyState) {
    await storeTitleLogs.removeFromState(titleData, historyState)
  }

  emit('statusChanged', { status: null })
}

// Remove the watchlist intent only, leaving history logs alone
const removeWatchlistIntent = async () => {
  if (!props.title) return

  const tmdbType = props.title.media_type || props.titleType
  const titleData = { id: props.title.id, type: tmdbType }
  const hadIntentLog = storeTitleLogs.isWatchlistedByTmdb(
    props.title.id,
    tmdbType
  )

  await storeTitleLogs.removeFromState(titleData, 'watchlist')

  // If watchlist status came from list system fallback (no log entry),
  // remove directly from the list system
  if (!hadIntentLog) {
    const watchlist = storeLists.getSystemListByType('watchlist')
    if (watchlist) {
      const item = storeLists.items.find(
        (i) =>
          i.list_id === watchlist.id &&
          i.titles?.tmdb_id == props.title.id &&
          i.titles?.tmdb_type == tmdbType
      )
      if (item) {
        await storeLists.removeItemFromList(watchlist.id, item.id)
      }
    }
  }
}

// Clear ALL statuses (for duplicate cleanup)
const clearAllStatuses = async () => {
  if (!props.title) return
  isOpen.value = false
  haptics.medium()

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  try {
    await storeTitleLogs.clearAllStatuses(titleData)
    emit('statusChanged', { status: null, clearedAll: true })
  } catch (error) {
    console.error('Failed to clear all statuses:', error)
  }
}

// Get readable state name
const getStateName = (state) => {
  switch (state) {
    case 'seen':
      return 'Seen'
    case 'in_progress':
      return 'Watching'
    case 'watchlist':
      return 'Watchlist'
    case 'dropped':
      return 'Did not finish'
    default:
      return state
  }
}
</script>
